import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { FaGithub, FaLinkedin } from "react-icons/fa";
import { contributionService, Contributor, uploadAvatarImage } from "../../lib/firebase";


// --- AI-Powered Reviews Section Components ---
// All the logic and components for the new section are placed here.

const reviewsData = [
    {
        username: 'cyb3r_ninja',
        userTitle: 'Senior Penetration Tester',
        imgSrc: 'https://placehold.co/100x100/1F2937/FFFFFF?text=CN',
        content: 'The "Straight Up" methodology is a lifesaver. It gives me a comprehensive checklist without the fluff. RAWPA is now my go-to for structuring my engagements and making sure I don\'t miss a step.'
    },
    {
        username: 'EthicalHacker22',
        userTitle: 'Cybersecurity Student',
        imgSrc: 'https://placehold.co/100x100/1F2937/FFFFFF?text=EH',
        content: 'The "Pathway" mode is brilliant. When I found a subdomain, it knew to suggest Nmap next. It\'s teaching me the *why* behind the steps.'
    },
    {
        username: 'HackTrails',
        userTitle: 'Cybersecurity Analyst',
        imgSrc: 'https://placehold.co/100x100/1F2937/FFFFFF?text=SD',
        content: 'Sounds like it could be really useful for beginners in helping them find their way and getting used to a workflow.'
    },
    {
        username: 'Karthik Patted',
        userTitle: 'Cybersecurity Analyst | Penetration Tester',
        imgSrc: 'https://placehold.co/100x100/1F2937/FFFFFF?text=RT',
        content: '...At a high level, the UI feels clean, modern, and focused. I like the simplicity and directness of the landing page...'
    },
    {
        username: 'Umer Khanzada',
        userTitle: 'Penetration Tester |NASA Hall Of Fame',
        imgSrc: 'https://placehold.co/100x100/1F2937/FFFFFF?text=IG',
        content: 'The UI is clean, modern, and easy to follow.I really like the focus on methodology, tool suggestions, and command prompts—super helpful for structured testing.'
    },
    {
        username: 'Mst. Awalunnisa',
        userTitle: 'Divisional Cybersecurity Leader(Dhaka) @ bTb',
        imgSrc: 'https://placehold.co/100x100/1F2937/FFFFFF?text=BB',
        content: 'Love the vision behind RAWPA, Its refreshing to see a pentesting tool that focuses on guidance rather than just automation. The way youre balancing ambitious AI features with core methodology work shows real developer wisdom. Those gamification ideas sound like theyll make security testing way more engaging too. Cant wait to see where you take this next!'
    }
];
const knownTools = ['Nmap', 'ffuf', 'sqlmap', 'IDOR'];

// Modal Component for the Learning Path
type LearningPathStep = { step: number; title: string; description: string };

interface LearningPathModalProps {
    isOpen: boolean;
    onClose: () => void;
    learningPath: LearningPathStep[];
    isLoading: boolean;
}

const LearningPathModal: React.FC<LearningPathModalProps> = ({ isOpen, onClose, learningPath, isLoading }) => {
    if (!isOpen) return null;

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
           
                {isLoading ? (
                    <div className="loading-spinner"></div>
                ) : (
                    <div className="path-steps">
                        {learningPath.map((step: LearningPathStep, index: number) => (
                            <div key={index} className="path-step">
                                <div className="step-number">{step.step}</div>
                                <div className="step-details">
                                    <h4>{step.title}</h4>
                                    <p>{step.description}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

// Review Card Component
interface ReviewCardProps {
    username: string;
    userTitle: string;
    imgSrc: string;
    content: string;
}

const ReviewCard: React.FC<ReviewCardProps> = (props) => {
    const { username, userTitle, imgSrc, content } = props;
    const [tooltipData, setTooltipData] = useState<{ visible: boolean; text: string; target: HTMLElement | null }>({ visible: false, text: '', target: null });
    const cardRef = useRef<HTMLDivElement | null>(null);

    const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
        const target = e.target as HTMLImageElement;
        target.onerror = null;
        target.src = 'https://placehold.co/100x100/374151/FFFFFF?text=??';
    };

    const handleToolExplanation = async (e: React.MouseEvent<HTMLButtonElement>, tool: string) => {
        e.preventDefault();
        e.stopPropagation();
        const target = e.currentTarget;
        setTooltipData({ visible: true, text: 'Loading...', target });

        const prompt = `In one short sentence, what is the cybersecurity tool or concept "${tool}" used for?`;
        
        try {
            const apiKey = "AIzaSyByfQIA4lBPbasxkvpT87KV2WKkwW9sXLU";
            const apiUrl = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${apiKey}`;
            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ contents: [{ parts: [{ text: prompt }] }] })
            });
            const result = await response.json();
            const text = result.candidates?.[0]?.content?.parts?.[0]?.text || "Could not get explanation.";
            setTooltipData({ visible: true, text, target });
        } catch (error) {
            console.error("Tool explanation error:", error);
            setTooltipData({ visible: true, text: 'Error fetching explanation.', target });
        }
    };
    
    useEffect(() => {
        const currentCard = cardRef.current;
        const handleMouseLeave = () => setTooltipData({ visible: false, text: '', target: null });
        if (currentCard) {
            currentCard.addEventListener('mouseleave', handleMouseLeave);
            return () => currentCard.removeEventListener('mouseleave', handleMouseLeave);
        }
        return () => {};
    }, []);
    
    const renderContentWithTools = (text: string) => {
        const regex = new RegExp(`\\b(${knownTools.join('|')})\\b`, 'gi');
        const parts = text.split(regex);
        return parts.map((part: string, index: number) => {
            if (knownTools.map(t => t.toLowerCase()).includes(part.toLowerCase())) {
                return (
                    <span key={index} className="tool-highlight">
                        {part}
                        <button className="tool-explain-button" onClick={(e) => handleToolExplanation(e, part)}>✨</button>
                    </span>
                );
            }
            return part;
        });
    };
    
    const getTooltipPosition = () => {
        if (!tooltipData.visible || !tooltipData.target || !cardRef.current) return { display: 'none' } as React.CSSProperties;
        const cardRect = cardRef.current.getBoundingClientRect();
        const targetRect = tooltipData.target.getBoundingClientRect();
        const top = targetRect.top - cardRect.top - 8;
        const left = targetRect.left - cardRect.left + (targetRect.width / 2);
        return { position: 'absolute' as const, top: `${top}px`, left: `${left}px`, transform: 'translate(-50%, -100%)' };
    };

    return (
        <div className="review-card" ref={cardRef}>
            <div className="profile">
                <img src={imgSrc} alt={username} className="profile-img" onError={handleImageError} />
                <div className="profile-info">
                    <h3 className="username">{username}</h3>
                    <p className="user-title">{userTitle}</p>
                </div>
            </div>
            <p className="content">"{renderContentWithTools(content)}"</p>
            {tooltipData.visible && <div className="tooltip" style={getTooltipPosition()}>{tooltipData.text}</div>}
        </div>
    );
};

// Main Scrolling Component for Reviews
const ReviewsScroller = () => {
    const [scrollingReviews, setScrollingReviews] = useState<ReviewCardProps[]>([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [learningPath, setLearningPath] = useState<LearningPathStep[]>([]);
    const [isLoadingPath, setIsLoadingPath] = useState(false);

    useEffect(() => {
        setScrollingReviews([...reviewsData, ...reviewsData]);
    }, []);
    
    const handleGeneratePath = async () => {
        setIsModalOpen(true);
        setIsLoadingPath(true);
        setLearningPath([]);

        const reviewsText = reviewsData.map(r => r.content).join('\n');
        const prompt = `Based on the following user reviews for the RAWPA pentesting application, identify the key tools and concepts mentioned. Then, generate a beginner-friendly, 3-step learning path.
        Reviews:\n${reviewsText}\n
        Respond ONLY with a JSON object in the following format: [{"step": 1, "title": "Step Title", "description": "Step Description"}, ...]. Do not include any other text or markdown formatting.`;
        
        try {
            const apiKey = ""; 
            const apiUrl = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${apiKey}`;
            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ contents: [{ parts: [{ text: prompt }] }] })
            });
            let resultText = (await response.json()).candidates[0].content.parts[0].text;
            resultText = resultText.replace(/```json/g, '').replace(/```/g, '').trim();
            const path: LearningPathStep[] = JSON.parse(resultText);
            setLearningPath(path);
        } catch (error) {
            console.error("Learning path error:", error);
            setLearningPath([{step: 1, title: "Error", description: "Could not generate a learning path. Please try again."}]);
        } finally {
            setIsLoadingPath(false);
        }
    };

    return (
        <section className="font-sans">
             <style>{`
                /* Themed styles for the reviews section */
                .reviews-container { background-color: transparent; width: 100%; max-width: 100%; padding: 4rem 0; position: relative; overflow: hidden; }
                .reviews-container h2 { font-family: 'Space Grotesk', sans-serif; font-size: 2.5rem; font-weight: 700; text-align: center; margin-bottom: 1rem; color: #111827; letter-spacing: -0.5px; }
                .reviews-container::before, .reviews-container::after { content: ''; position: absolute; top: 0; width: 200px; height: 100%; z-index: 2; }
                .reviews-container::before { left: 0; background: linear-gradient(to right, #e1dcdc75, rgba(225, 220, 220, 0)); }
                .reviews-container::after { right: 0; background: linear-gradient(to left, #e1dcdc75, rgba(225, 220, 220, 0)); }

                /* Scroller Animation */
                .reviews-scroller { display: flex; width: max-content; flex-wrap: nowrap; animation: scroll 60s linear infinite; }
                .reviews-scroller:hover { animation-play-state: paused; }
                @keyframes scroll { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }

                /* Review Card */
                .review-card {
                    position: relative; flex: 0 0 auto; width: 380px; margin: 0 1rem; padding: 2rem;
                    background-color: #ffffffad; /* Match feature cards */
                    border-radius: 20px;
                    border: 1px solid #E5E7EB;
                    backdrop-filter: blur(4px);
                    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03);
                    transition: transform 0.3s ease, box-shadow 0.3s ease;
                    display: flex; flex-direction: column;
                }
                .review-card:hover { transform: translateY(-8px); box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.07), 0 4px 6px -2px rgba(0, 0, 0, 0.05); }
                .review-card .profile { display: flex; align-items: center; margin-bottom: 1.5rem; }
                .review-card .profile-img { width: 50px; height: 50px; border-radius: 50%; margin-right: 1rem; border: 2px solid #E5E7EB; object-fit: cover; }
                .review-card .profile-info { line-height: 1.3; }
                .review-card .username { font-family: 'Space Grotesk', sans-serif; font-weight: 600; color: #111827; margin: 0; }
                .review-card .user-title { font-family: 'Space Grotesk', sans-serif; font-size: 0.875rem; color: #6B7280; margin: 0; }
                .review-card .content { font-family: 'Space Grotesk', sans-serif; font-size: 1rem; line-height: 1.6; color: #4B5563; flex-grow: 1; }

                /* Tool Highlighting & Tooltip */
                .tool-highlight { color: #4338ca; font-weight: 500; }
                .tool-explain-button { background: none; border: none; color: #6B7280; cursor: pointer; display: inline-block; vertical-align: super; font-size: 0.8em; margin-left: 2px; padding: 0; transition: color 0.2s; }
                .tool-explain-button:hover { color: #daed6e; }
                .tooltip { font-family: 'Space Grotesk', sans-serif; position: absolute; background-color: #1F2937; color: white; padding: 8px 12px; border-radius: 6px; font-size: 0.85rem; z-index: 10; width: 220px; text-align: center; pointer-events: none; box-shadow: 0 2px 8px rgba(0,0,0,0.2); }
                
                /* Learning Path Button */
                .path-button-container { text-align: center; margin-bottom: 3rem; }
                .learning-path-button {
                    background-color: #daed6e;
                    color: #1F2937;
                    border: 1px solid #e7e7e7;
                    padding: 12px 28px;
                    border-radius: 20px;
                    font-family: 'Space Grotesk', sans-serif;
                    font-size: 1.25rem;
                    font-weight: 700;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
                }
                .learning-path-button:hover { background-color: #c5d85c; transform: scale(1.05); }
                
                /* Modal Styling */
                .modal-overlay { font-family: 'Space Grotesk', sans-serif; position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0, 0, 0, 0.5); display: flex; align-items: center; justify-content: center; z-index: 1000; backdrop-filter: blur(4px); }
                .modal-content { background: #FFFFFF; color: #1F2937; padding: 2rem; border-radius: 1rem; width: 90%; max-width: 600px; position: relative; box-shadow: 0 10px 25px rgba(0,0,0,0.1); border: 1px solid #E5E7EB; }
                .modal-title { margin-top: 0; margin-bottom: 1.5rem; font-size: 1.5rem; text-align: center; }
                .modal-close-button { position: absolute; top: 1rem; right: 1rem; background: none; border: none; color: #9CA3AF; font-size: 1.5rem; cursor: pointer; }
                .loading-spinner { border: 4px solid #E5E7EB; border-top: 4px solid #daed6e; border-radius: 50%; width: 40px; height: 40px; animation: spin 1s linear infinite; margin: 2rem auto; }
                @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
                .path-steps { display: flex; flex-direction: column; gap: 1.5rem; }
                .path-step { display: flex; align-items: flex-start; gap: 1rem; }
                .step-number { background-color: #daed6e; color: #1F2937; border-radius: 50%; width: 32px; height: 32px; display: flex; align-items: center; justify-content: center; font-weight: 700; flex-shrink: 0; }
                .step-details h4 { margin: 0 0 0.25rem 0; color: #111827; font-weight: 600; }
                .step-details p { margin: 0; color: #4B5563; line-height: 1.5; }

                /* Responsive */
                @media (max-width: 768px) {
                    .review-card { width: 320px; padding: 1.5rem; }
                    .reviews-container h2 { font-size: 2rem; }
                    .reviews-container::before, .reviews-container::after { width: 100px; }
                }
            `}</style>
            
            <div className="reviews-container">
                <footer className="w-full py-12 flex justify-center items-center bg-transparent mt-auto">
      {/* mt-auto pushes the footer to the bottom in a flex column layout */}
      <div className="flex items-center space-x-6">
        {/* GitHub Icon Link */}
      
                <h2>What Our Users Are Saying</h2>
                
                <div className="reviews-scroller">
                    {scrollingReviews.map((review, index) => (
                        <ReviewCard key={index} username={review.username} userTitle={review.userTitle} imgSrc={review.imgSrc} content={review.content} />
                    ))}
                </div>
            </div>
            <LearningPathModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} learningPath={learningPath} isLoading={isLoadingPath} />
        </footer>
            </div>
        </section>
    );
};

// --- Contributors Section ---
const ContributorsSection = () => {
  const [contributors, setContributors] = useState<Contributor[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({
    type: '',
    details: '',
    contributorName: '',
    linkedin: '',
    twitter: '',
    reddit: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [avatar, setAvatar] = useState<string | null>(null);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);

  useEffect(() => {
    contributionService.getRecentContributors(5).then(setContributors);
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    setSuccess(false);
    try {
      let avatarUrl = undefined;
      if (avatarFile) {
        avatarUrl = await uploadAvatarImage(avatarFile, form.contributorName || 'anonymous');
      }
      const contributionId = await contributionService.submitContribution({
        type: form.type as any,
        details: form.details,
        contributorName: form.contributorName,
        linkedin: form.linkedin,
        twitter: form.twitter,
        reddit: form.reddit,
        avatar: avatarUrl,
      });
      setSuccess(true);
      setForm({ type: '', details: '', contributorName: '', linkedin: '', twitter: '', reddit: '' });
      setAvatar(null);
      setAvatarFile(null);
      console.log('Contribution submitted successfully. ID:', contributionId);
    } catch (err: any) {
      console.error('Contribution submit error:', err);
      setError(err?.message || 'Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  function getInitials(name: string) {
    if (!name) return "?";
    const parts = name.split(" ");
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  function getRandomAnon() {
    return `Anonymous-${Math.floor(10000 + Math.random() * 90000)}`;
  }

  return (
    <section className="my-16">
      <div className="max-w-3xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl border border-[#e7e7e7] p-8 mb-8 hover:shadow-2xl transition-shadow">
          <h2 className="text-2xl font-bold text-center mb-6 text-[#1F2937] tracking-tight">Recent Contributors</h2>
          <div className="flex flex-wrap justify-center gap-4 mb-6">
            {contributors.length === 0 && <span className="text-gray-500">No contributors yet.</span>}
            {contributors.map((c, idx) => {
              let name = c.name?.trim() || '';
              if (!name) name = getRandomAnon();
              let initials = getInitials(name);
              return (
                <div key={c.id || idx} className="bg-white rounded-lg shadow p-4 min-w-[180px] text-center flex flex-col items-center">
                  <div className="w-14 h-14 rounded-full flex items-center justify-center mb-2 overflow-hidden border-2 border-[#daed6e]" style={{background: 'linear-gradient(135deg, #daed6e 60%, #e1dcdc 100%)'}}>
                    {c.avatar ? (
                      <img 
                        src={c.avatar} 
                        alt={name} 
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                          target.nextElementSibling?.classList.remove('hidden');
                        }}
                      />
                    ) : null}
                    <span className={`text-xl font-bold text-gray-800 ${c.avatar ? 'hidden' : ''}`}>{initials}</span>
                  </div>
                  <div className="font-semibold">{name}</div>
                  <div className="text-xs text-gray-500 mb-1">{c.contributionType}</div>
                  <div className="flex justify-center gap-2">
                    {c.linkedin && <a href={c.linkedin} target="_blank" rel="noopener noreferrer" className="text-blue-700">LinkedIn</a>}
                    {c.twitter && <a href={c.twitter} target="_blank" rel="noopener noreferrer" className="text-blue-400">Twitter</a>}
                    {c.reddit && <a href={c.reddit} target="_blank" rel="noopener noreferrer" className="text-orange-600">Reddit</a>}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex justify-center mb-4">
            <Button className="bg-[#daed6e] rounded-[20px] px-8 py-2 font-bold" onClick={() => setShowModal(true)}>
              Contribute
            </Button>
          </div>
          <div className="text-center text-sm text-gray-600 mb-2">
            <a href="/contributors" className="underline">See all contributors</a>
          </div>
          {showModal && (
            <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg p-8 max-w-md w-full relative">
                <button className="absolute top-2 right-2 text-gray-400 hover:text-black" onClick={() => setShowModal(false)}>&times;</button>
                <h3 className="text-xl font-bold mb-2">Submit a Contribution</h3>
                <div className="mb-2 text-xs text-gray-500">
                  <b>Guidelines:</b> Please provide clear, actionable, and original content. All submissions are reviewed by the RAWPA team. Accepted contributors will be listed publicly. Spam or low-effort submissions will be rejected.
                </div>
                <form onSubmit={handleSubmit} className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium">Type of Contribution*</label>
                    <select name="type" value={form.type} onChange={handleChange} required className="w-full border rounded p-2">
                      <option value="">Select type</option>
                      <option value="methodology">Methodology/Guide</option>
                      <option value="toolkit">Toolkit Resource</option>
                      <option value="data">Data/Information</option>
                      <option value="bug">Bug Report</option>
                      <option value="feature">Feature Request</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium">Contribution Details*</label>
                    <textarea name="details" value={form.details} onChange={handleChange} required className="w-full border rounded p-2 min-h-[80px]" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium">Your Name (optional)</label>
                    <input name="contributorName" value={form.contributorName} onChange={handleChange} className="w-full border rounded p-2" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium">Avatar (optional)</label>
                    <input type="file" accept="image/*" onChange={e => {
                      const file = e.target.files?.[0];
                      if (file) {
                        setAvatarFile(file);
                        setAvatar(URL.createObjectURL(file));
                      }
                    }} />
                    {avatar && <img src={avatar} alt="avatar preview" className="w-16 h-16 rounded-full mt-2" />}
                  </div>
                  <div className="flex gap-2">
                    <input name="linkedin" value={form.linkedin} onChange={handleChange} placeholder="LinkedIn" className="w-full border rounded p-2" />
                    <input name="twitter" value={form.twitter} onChange={handleChange} placeholder="Twitter" className="w-full border rounded p-2" />
                    <input name="reddit" value={form.reddit} onChange={handleChange} placeholder="Reddit" className="w-full border rounded p-2" />
                  </div>
                  <div className="text-xs text-gray-500">
                    All contributions are reviewed privately by the team before being published.
                  </div>
                  {error && <div className="text-red-500 text-xs">{error}</div>}
                  {success && <div className="text-green-600 text-xs">Thank you for your contribution! It will be reviewed soon.</div>}
                  <Button type="submit" className="w-full bg-[#daed6e] hover:bg-[#c5d85c] text-black" disabled={submitting}>
                    {submitting ? 'Submitting...' : 'Submit'}
                  </Button>
                </form>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

// --- Main Landing Page Component ---
export const LandingPage = (): JSX.Element => {
  const navigate = useNavigate();
  const [contributors, setContributors] = useState<Contributor[]>([]);

  useEffect(() => {
    contributionService.getRecentContributors(5).then(setContributors);
  }, []);

  function getInitials(name: string) {
    if (!name) return "?";
    const parts = name.split(" ");
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  function getRandomAnon() {
    return `Anonymous-${Math.floor(10000 + Math.random() * 90000)}`;
  }

  const featureCards = [
    {
      title: "Hierarchical Methodology",
      description: "A step-by-step methodology to follow.",
      icon: "/script-2--language-programming-code.png",
      iconAlt: "Script language",
    },
    {
      title: "Tool Suggestions",
      description: "The right tool is suggested for the right task.",
      icon: "/wrench--crescent-tool-construction-tools-wrench-setting-edit-adj.png",
      iconAlt: "Wrench crescent tool",
    },
    {
      title: "Actionable Commands",
      description: "The commands for the tools to gain the best results.",
      icon: "/desktop-code--desktop-device-display-computer-code-terminal-html.png",
      iconAlt: "Desktop code desktop",
    },
    {
      title: "Hacker's Checklists",
      description: "Battle-tested checklists to keep your recon, exploitation, and reporting flows consistent.",
      icon: "/security-icon.svg",
      iconAlt: "Checklist icon",
    },
    {
      title: "Contribution System",
      description: "Anyone can contribute guides, data, or resources to help the community.",
      icon: "/user-icon.svg",
      iconAlt: "Contribution system",
    },
    {
      title: "Hunting Toolkit",
      description: "Curated cybersecurity tools and commands for your penetration testing engagements.",
      icon: "/security-icon.svg",
      iconAlt: "Hunting toolkit",
    },
    {
      title: "OSINT Toolkit",
      description: "A collection of essential OSINT tools, commands, and links for reconnaissance and information gathering.",
      icon: "/rectangle-4.svg",
      iconAlt: "OSINT toolkit",
    },
    {
      title: "Integration of LOLBAS, GTFOBins, and WADComs",
      description: "Directly access and utilize LOLBAS, GTFOBins, and WADComs resources within RAWPA for advanced attack simulation and defense.",
      icon: "/wrench--crescent-tool-construction-tools-wrench-setting-edit-adj.png",
      iconAlt: "Integration resources",
    },
    // AAweRT Card
    {
      title: "Introduction of AAweRT - An Awesome Reconnaissance Tool",
      description: "A collection of powerful, open-source tools to efficiently discover and analyze potential attack surfaces. Designed with RAWPA in mind.",
      icon: "/beetle.png",
      iconAlt: "AAweRT logo",
      link: "https://github.com/Kuwguap/aawert"
    },
    // Hunters Board Card
    {
      title: "Hunters Board - Kanban-Style Project Management",
      description: "Organize your pentesting projects with a modern Kanban board. Track findings, manage methodology steps, and collaborate with your team in real-time.",
      icon: "/script-2--language-programming-code.png",
      iconAlt: "Hunters Board",
    },
  ];

  return (
    <div className="min-h-screen w-full bg-[#e1dcdc75] relative overflow-x-hidden">
      {/* Navigation bar */}
      <div className="fixed w-full px-4 top-[46px] z-10">
        <div className="max-w-[1257px] mx-auto h-[77px] relative rounded-[28px]">
          <div className="absolute w-full h-full top-0 left-0 bg-white rounded-[28px] shadow-[0px_4px_4px_#00000040] blur-[3.05px] opacity-[0.53]" />
          <div className="relative h-full flex items-center justify-between px-6">
            <div className="flex items-center gap-4">
              <img className="w-[27px] h-[27px] object-cover" alt="Beetle" src="/beetle.png" />
              <span className="font-['Space_Grotesk'] font-bold text-black text-[32px]">RAWPA</span>
            </div>
            <Button 
              className="w-[166px] h-[49px] bg-[#daed6e] rounded-[20px] border border-solid border-[#e7e7e7] hover:bg-[#c5d85c]"
              onClick={() => navigate('/join')}
            >
              <span className="font-['Space_Grotesk'] font-bold text-black text-[32px]">JOIN</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="container mx-auto px-4 pt-[160px] relative pb-16">
        <div className="absolute w-[197px] h-[197px] top-[237px] right-[20%] bg-[#e7e71154] rounded-[98.5px] blur-[5px] opacity-[0.57]" />
        
        {/* Version badge */}
        <div className="flex justify-center mb-4">
          <Badge className="bg-[#daed6e40] rounded-[20px] border border-solid border-[#daed6ebd]">
            <div className="[-webkit-text-stroke:1px_#daed6ebd] font-['Space_Grotesk'] font-light text-[#000000d4] text-xl">
              v1.0.0
            </div>
          </Badge>
        </div>

        {/* Main heading */}
        <div className="text-center mb-8 px-4">
          <h1 className="font-['Space_Grotesk'] font-bold text-black text-5xl sm:text-6xl md:text-7xl lg:text-8xl xl:text-[80px] leading-tight [text-shadow:0px_4px_4px_#00000040] max-w-full">
            Navigating the <span className="[text-shadow:-4px_6px_19.6px_#daed6ebd]">"So What Now?"</span> of CyberSecurity.
          </h1>
        </div>

        {/* Description */}
        <div className="max-w-[672px] mx-auto text-center mb-8 px-4">
          <p className="font-['Space_Grotesk'] text-xl sm:text-2xl md:text-2xl [text-shadow:0px_4px_4px_#00000040]">
            RAWPA-Rodney the Advanced Web Penetration Assistant provides
            hierarchical methodology, interactive pathways, comprehensive toolkits,
            and guided cybersecurity workflows for penetration testing and security research.
          </p>
        </div>

        {/* Video Embed */}
        <div className="mb-12">
          <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0 }}>
            <iframe
              src="https://www.loom.com/embed/fbc2a413846948e993adcc0d090bd40f?sid=024b8fca-76d9-4c32-bb6a-c526971fa3cd"
              frameBorder="0"
              allowFullScreen
              style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', borderRadius:25 }}
              title="RAWPA Demo Video"
            ></iframe>
          </div>
        </div>
        
        {/* Waitlist Signup */}
        <div className="flex flex-col items-center gap-4 mb-24">
          <p className="opacity-[0.54] font-['Space_Grotesk'] font-medium text-base">
            Simplify your pentesting workflow now with RAWPA
          </p>
          <div className="flex justify-center">
            <a
              href="/join"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#daed6e] rounded-[20px] hover:bg-[#c5d85c] font-bold text-gray-800 whitespace-nowrap text-lg transition-colors duration-200 ease-in-out shadow-md"
            >
              <img className="w-[17px] h-[15px]" alt="Arrow" src="/arrow-1.svg" />
              <span>Join RAWPA</span>
            </a>
          </div>
        </div>

        {/* Feature cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-[1200px] mx-auto mb-24">
          {featureCards.map((card, index) => (
            <Card key={index} className="bg-[#ffffffad] rounded-[20px] backdrop-blur-sm">
              <CardContent className="p-6">
                <div className="flex items-start gap-2 mb-4">
                  <img
                    className="w-[19px] h-[19px]"
                    alt={card.iconAlt}
                    src={card.icon}
                  />
                  <h3 className="font-['Space_Grotesk'] font-medium text-black text-xl">
                    {card.title}
                  </h3>
                </div>
                <p className="font-['Space_Grotesk'] font-light text-black text-base mb-2">
                  {card.description}
                </p>
                {card.link && (
                  <a
                    href={card.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block mt-2 px-4 py-2 bg-[#daed6e] rounded-[12px] text-black font-semibold hover:bg-[#c5d85c] transition-colors"
                  >
                    Access AAweRT
                  </a>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        {/* --- AI-Powered Reviews Section --- */}
        <ReviewsScroller />

        {/* --- Contributors Section (now after reviews) --- */}
        <ContributorsSection />
        
        {/* Footer at the very bottom of the landing page */}
        <footer className="w-full py-12 flex justify-center items-center bg-transparent mt-auto">
          {/* mt-auto pushes the footer to the bottom in a flex column layout */}
          <div className="flex items-center space-x-6">
            {/* GitHub Icon Link */}
            <a
              href="https://github.com/Kuwguap/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="GitHub Profile"
              className="
                flex items-center justify-center
                w-12 h-12
                rounded-full
                bg-zinc-900
                text-white
                text-4xl
                transition-all
                duration-300
                ease-in-out
                hover:bg-[#cfff0f]
                hover:text-zinc-900
                hover:scale-110
                hover:-translate-y-1
                shadow-md
              "
            >
              <FaGithub />
            </a>
            {/* LinkedIn Icon Link */}
            <a
              href="https://www.linkedin.com/in/glenn-osioh-85104827b/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="LinkedIn Profile"
              className="
                flex items-center justify-center
                w-12 h-12
                rounded-full
                bg-zinc-900
                text-white
                text-4xl
                transition-all
                duration-300
                ease-in-out
                hover:bg-[#0077b5]
                hover:text-white
                hover:scale-110
                hover:-translate-y-1
                shadow-md
              "
            >
              <FaLinkedin />
            </a>
            {/* Blog Link with Beetle Icon */}
            <a
              href="https://kuwguap.github.io/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Rodney's Intuition Blog"
              className="
                flex items-center justify-center
                w-12 h-12
                rounded-full
                bg-zinc-900
                text-white
                text-4xl
                transition-all
                duration-300
                ease-in-out
                hover:bg-[#e1dcdc]
                hover:scale-110
                hover:-translate-y-1
                shadow-md
              "
            >
              <img src="/logo.png" alt="Blog" className="w-8 h-8 object-cover rounded-full" />
            </a>
          <a
              href="https://coff.ee/kuwguap"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Buy me a coffee"
              className="
               flex items-center justify-center
                w-12 h-12
                rounded-full
                bg-zinc-900
                text-white
                text-4xl
                transition-all
                duration-300
                ease-in-out
                hover:bg-[yellow]
                hover:scale-110
                hover:-translate-y-1
                shadow-md
              "
              title="Buy me a coffee"
            >
              <span role="img" aria-label="coffee">☕</span>
            </a>
            </div>
        </footer>
      </div>
    </div>
  );
};